import React, { useRef, useEffect, useState } from "react";

const COLS = 10;
const ROWS = 20;
const BLOCK = 24; // pixel size of each block
const START_SPEED = 800;
const LEVEL_UP_EVERY = 10;

const TETROMINOES = {
  I: [
    [[-2,0],[-1,0],[0,0],[1,0]],
    [[0,-1],[0,0],[0,1],[0,2]],
    [[-2,1],[-1,1],[0,1],[1,1]],
    [[-1,-1],[-1,0],[-1,1],[-1,2]],
  ],
  O: [
    [[0,0],[1,0],[0,1],[1,1]],
    [[0,0],[1,0],[0,1],[1,1]],
    [[0,0],[1,0],[0,1],[1,1]],
    [[0,0],[1,0],[0,1],[1,1]],
  ],
  T: [
    [[-1,0],[0,0],[1,0],[0,1]],
    [[0,-1],[0,0],[0,1],[1,0]],
    [[-1,0],[0,0],[1,0],[0,-1]],
    [[0,-1],[0,0],[0,1],[-1,0]],
  ],
  S: [
    [[-1,1],[0,1],[0,0],[1,0]],
    [[0,-1],[0,0],[1,0],[1,1]],
    [[-1,1],[0,1],[0,0],[1,0]],
    [[0,-1],[0,0],[1,0],[1,1]],
  ],
  Z: [
    [[-1,0],[0,0],[0,1],[1,1]],
    [[1,-1],[1,0],[0,0],[0,1]],
    [[-1,0],[0,0],[0,1],[1,1]],
    [[1,-1],[1,0],[0,0],[0,1]],
  ],
  J: [
    [[-1,0],[-1,1],[0,0],[1,0]],
    [[0,-1],[0,0],[0,1],[1,1]],
    [[-1,0],[0,0],[1,0],[1,-1]],
    [[-1,-1],[0,-1],[0,0],[0,1]],
  ],
  L: [
    [[-1,0],[0,0],[1,0],[1,1]],
    [[0,-1],[0,0],[0,1],[1,-1]],
    [[-1,-1],[-1,0],[0,0],[1,0]],
    [[-1,1],[0,-1],[0,0],[0,1]],
  ],
};

const COLORS = {
  I: "#00f0f0",
  O: "#f0f000",
  T: "#a000f0",
  S: "#00f000",
  Z: "#f00000",
  J: "#0000f0",
  L: "#f0a000",
  X: "#444444",
};

function randomPiece() {
  const keys = Object.keys(TETROMINOES);
  const type = keys[Math.floor(Math.random() * keys.length)];
  return {
    type,
    rot: 0,
    x: Math.floor(COLS / 2),
    y: 0,
  };
}

function createEmptyBoard() {
  return Array.from({ length: ROWS }, () => Array(COLS).fill(null));
}

export default function Tetris() {
  const canvasRef = useRef(null);
  const nextCanvasRef = useRef(null);

  // board state and ref
  const [board, setBoard] = useState(createEmptyBoard());
  const boardRef = useRef(board);
  const setBoardAndRef = (newBoard) => {
    boardRef.current = newBoard;
    setBoard(newBoard);
  };

  // piece refs
  const pieceRef = useRef(randomPiece());
  const nextRef = useRef(randomPiece());

  // game state
  const [score, setScore] = useState(0);
  const [lines, setLines] = useState(0);
  const [level, setLevel] = useState(0);
  const [running, setRunning] = useState(true);
  const [tick, setTick] = useState(0);
  const dropIntervalRef = useRef(null);

  // drawing helpers
  const drawBlock = (ctx, x, y, color) => {
    const px = x * BLOCK;
    const py = y * BLOCK;
    ctx.fillStyle = color;
    ctx.fillRect(px + 1, py + 1, BLOCK - 2, BLOCK - 2);
    ctx.strokeStyle = "#222";
    ctx.strokeRect(px + 1, py + 1, BLOCK - 2, BLOCK - 2);
  };

  const draw = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // draw board
    const curBoard = boardRef.current;
    for (let r = 0; r < ROWS; r++) {
      for (let c = 0; c < COLS; c++) {
        const t = curBoard[r][c];
        if (t) {
          drawBlock(ctx, c, r, COLORS[t] || COLORS.X);
        } else {
          ctx.fillStyle = "#111";
          ctx.fillRect(c * BLOCK, r * BLOCK, BLOCK, BLOCK);
          ctx.strokeStyle = "#0b0b0b";
          ctx.strokeRect(c * BLOCK, r * BLOCK, BLOCK, BLOCK);
        }
      }
    }

    // draw falling piece
    const piece = pieceRef.current;
    const shape = TETROMINOES[piece.type][piece.rot];
    for (let b of shape) {
      const x = piece.x + b[0];
      const y = piece.y + b[1];
      if (y >= 0) drawBlock(ctx, x, y, COLORS[piece.type]);
    }

    // next preview
    const nc = nextCanvasRef.current;
    if (nc) {
      const nctx = nc.getContext("2d");
      nctx.clearRect(0, 0, nc.width, nc.height);
      const n = nextRef.current;
      const shapeN = TETROMINOES[n.type][0];
      const offsetX = 2;
      const offsetY = 2;
      for (let b of shapeN) {
        drawBlock(nctx, offsetX + b[0], offsetY + b[1], COLORS[n.type]);
      }
    }
  };

  // collision checks using the latest boardRef
  const collides = (piece, boardState = boardRef.current, xOff = 0, yOff = 0, rot = null) => {
    const rotIdx = rot === null ? piece.rot : rot;
    const shape = TETROMINOES[piece.type][rotIdx];
    for (let b of shape) {
      const x = piece.x + b[0] + xOff;
      const y = piece.y + b[1] + yOff;
      if (x < 0 || x >= COLS || y >= ROWS) return true;
      if (y >= 0 && boardState[y][x]) return true;
    }
    return false;
  };

  // lock piece and safely clear lines
  const lockPiece = () => {
    const p = pieceRef.current;
    const newBoard = boardRef.current.map(row => row.slice());

    // put piece into board
    const shape = TETROMINOES[p.type][p.rot];
    for (let b of shape) {
      const x = p.x + b[0];
      const y = p.y + b[1];
      if (y >= 0) {
        newBoard[y][x] = p.type;
      } else {
        // locked above the top => game over
        setRunning(false);
        clearInterval(dropIntervalRef.current);
        alert("Game Over!");
        return;
      }
    }

    // SAFER line clear: filter non-full rows, then add empty rows to top
    const filtered = newBoard.filter(row => !row.every(cell => cell !== null));
    const cleared = ROWS - filtered.length;
    for (let i = 0; i < cleared; i++) {
      filtered.unshift(Array(COLS).fill(null));
    }

    // scoring
    if (cleared > 0) {
      const scoreAdd = [0, 40, 100, 300, 1200][cleared] || cleared * 100;
      setScore(s => s + scoreAdd * (level + 1));
      setLines(l => l + cleared);
    }

    // commit board and spawn next piece
    setBoardAndRef(filtered);
    pieceRef.current = nextRef.current;
    nextRef.current = randomPiece();

    // immediate collision with new piece => game over
    if (collides(pieceRef.current)) {
      setRunning(false);
      clearInterval(dropIntervalRef.current);
      alert("Game Over!");
    }
  };

  // move piece down one cell (gravity or soft drop)
  const drop = () => {
    if (!running) return;
    const p = pieceRef.current;
    if (!collides(p, boardRef.current, 0, 1)) {
      p.y += 1;
    } else {
      lockPiece();
    }
    setTick(t => t + 1);
  };

  // movement helpers
  const move = (dx) => {
    const p = pieceRef.current;
    if (!collides(p, boardRef.current, dx, 0)) {
      p.x += dx;
      setTick(t => t + 1);
    }
  };

  const rotate = () => {
    const p = pieceRef.current;
    const newRot = (p.rot + 1) % 4;
    const kicks = [0, -1, 1, -2, 2];
    for (let k of kicks) {
      if (!collides(p, boardRef.current, k, 0, newRot)) {
        p.rot = newRot;
        p.x += k;
        setTick(t => t + 1);
        break;
      }
    }
  };

  const hardDrop = () => {
    const p = pieceRef.current;
    while (!collides(p, boardRef.current, 0, 1)) {
      p.y += 1;
    }
    lockPiece();
    setTick(t => t + 1);
  };

  // keyboard handlers
  useEffect(() => {
    const handler = (e) => {
      if (!running) return;
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        move(-1);
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        move(1);
      } else if (e.key === "ArrowDown") {
        e.preventDefault();
        drop(); // soft drop allowed repeatedly by key repeat
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        rotate();
      } else if (e.code === "Space") {
        e.preventDefault();
        hardDrop();
      } else if (e.key.toLowerCase() === "p") {
        e.preventDefault();
        togglePause();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [running]); // boardRef used directly, so no need to add board to deps

  // gravity interval uses boardRef and pieceRef so is robust to state timing
  useEffect(() => {
    if (!running) return;
    const speed = Math.max(100, START_SPEED - level * 60);
    clearInterval(dropIntervalRef.current);
    dropIntervalRef.current = setInterval(() => {
      drop();
    }, speed);
    return () => clearInterval(dropIntervalRef.current);
  }, [running, level]);

  // update level based on lines
  useEffect(() => {
    setLevel(Math.floor(lines / LEVEL_UP_EVERY));
  }, [lines]);

  // redraw whenever tick changes (we mutate pieceRef and boardRef directly)
  useEffect(() => {
    const c = canvasRef.current;
    if (c) {
      c.width = COLS * BLOCK;
      c.height = ROWS * BLOCK;
    }
    const nc = nextCanvasRef.current;
    if (nc) {
      nc.width = 6 * BLOCK;
      nc.height = 6 * BLOCK;
    }
    draw();
  }, [tick, board]); // include board so UI updates after setBoardAndRef as well

  const togglePause = () => {
    setRunning(r => !r);
    if (running) {
      clearInterval(dropIntervalRef.current);
    }
  };

  const restart = () => {
    const empty = createEmptyBoard();
    pieceRef.current = randomPiece();
    nextRef.current = randomPiece();
    setScore(0);
    setLines(0);
    setLevel(0);
    setRunning(true);
    setBoardAndRef(empty);
    setTick(t => t + 1);
  };

  return (
    <div style={{ display: "flex", gap: 16, alignItems: "flex-start", flexWrap: "wrap", justifyContent: "center" }}>
      <div>
        <canvas ref={canvasRef} style={{ background: "#000", imageRendering: "pixelated", display: "block" }} />
      </div>

      <div style={{ width: 160 }}>
        <div style={{ marginBottom: 8 }}>
          <strong>Next</strong>
          <canvas ref={nextCanvasRef} style={{ background: "#111", display: "block", marginTop: 6 }} />
        </div>

        <div style={{ marginBottom: 8 }}>
          <div>Score: <strong>{score}</strong></div>
          <div>Lines: <strong>{lines}</strong></div>
          <div>Level: <strong>{level}</strong></div>
        </div>

        <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
          <button onClick={togglePause} style={{ padding: "6px 8px" }}>{running ? "Pause" : "Resume"}</button>
          <button onClick={restart} style={{ padding: "6px 8px" }}>Restart</button>
        </div>

        <div style={{ fontSize: 12, opacity: 0.95 }}>
          <div><strong>Controls</strong></div>
          <div>← → move</div>
          <div>↓ soft drop</div>
          <div>↑ rotate</div>
          <div>Space hard drop</div>
          <div>P pause</div>
        </div>
      </div>
    </div>
  );
}
